import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { isStoppedCase } from '@/utils/caseFilters';
import { StopCircle, Eye } from 'lucide-react';
import axios from 'axios';

export default function StoppedCasesTab({ onViewCase }) {
  const [allCases, setAllCases] = useState([]);
  const [stoppedReasonFilter, setStoppedReasonFilter] = useState('all');
  const [stoppedDateFrom, setStoppedDateFrom] = useState('');
  const [stoppedDateTo, setStoppedDateTo] = useState('');

  const fetchAllCases = async () => {
    try {
      const res = await axios.get('/cases');
      setAllCases(res.data || []);
    } catch (e) { 
      console.log('Cases load failed'); 
    }
  };

  useEffect(() => { 
    fetchAllCases();
  }, []);

  // May 31, 2026 — broadened beyond exact `case.status === 'stopped'`
  // to also catch legacy 'stop_check' case-status and per-check stops
  // (where the case status stays as 'in_progress' / 'completed' but
  // one or more child checks have status 'stop_check'). Previously
  // production showed 0 stopped cases despite having many.
  const stoppedCases = allCases.filter(isStoppedCase);

  const getFilteredStoppedCases = () => {
    let filtered = stoppedCases;
    
    if (stoppedReasonFilter !== 'all') {
      filtered = filtered.filter(c => c.stop_reason === stoppedReasonFilter);
    }
    
    if (stoppedDateFrom) {
      filtered = filtered.filter(c => new Date(c.stopped_at || c.updated_at) >= new Date(stoppedDateFrom));
    }
    
    if (stoppedDateTo) {
      filtered = filtered.filter(c => new Date(c.stopped_at || c.updated_at) <= new Date(stoppedDateTo + 'T23:59:59'));
    }
    
    return filtered;
  };

  const getStopReasonLabel = (reason) => {
    const labels = {
      'candidate_no_show': 'Candidate No Show',
      'terminated': 'Terminated',
      'candidate_withdrew': 'Candidate Withdrew',
      'offer_revoked': 'Offer Revoked',
      'other': 'Other'
    };
    return labels[reason] || reason || 'N/A';
  };

  const filteredStoppedCases = getFilteredStoppedCases();

  return (
    <div data-testid="stopped-cases-tab">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Stopped Cases</h1>
          <p className="text-slate-600">View all stopped verification cases across all clients</p>
        </div>
        <Badge className="bg-red-100 text-red-700 text-lg px-4 py-2">
          {stoppedCases.length} Total Stopped
        </Badge>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-sm mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-sm text-slate-600">Stop Reason</Label>
              <Select value={stoppedReasonFilter} onValueChange={setStoppedReasonFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Reasons" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reasons</SelectItem>
                  <SelectItem value="candidate_no_show">Candidate No Show</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                  <SelectItem value="candidate_withdrew">Candidate Withdrew</SelectItem>
                  <SelectItem value="offer_revoked">Offer Revoked</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-slate-600">From Date</Label>
              <Input 
                type="date" 
                value={stoppedDateFrom} 
                onChange={(e) => setStoppedDateFrom(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm text-slate-600">To Date</Label>
              <Input 
                type="date" 
                value={stoppedDateTo} 
                onChange={(e) => setStoppedDateTo(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => { setStoppedReasonFilter('all'); setStoppedDateFrom(''); setStoppedDateTo(''); }}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stopped Cases Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StopCircle className="w-5 h-5 text-red-600" />
            Stopped Cases ({filteredStoppedCases.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Case ID</TableHead>
                <TableHead>Employee</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Stop Reason</TableHead>
                <TableHead>Stopped At</TableHead>
                <TableHead>Checks Stopped</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStoppedCases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    <StopCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No stopped cases found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredStoppedCases.map((caseItem) => {
                  const stoppedChecks = (caseItem.checks || []).filter(c => c.status === 'stop_check').length;
                  return (
                    <TableRow key={caseItem.id} className="hover:bg-slate-50">
                      <TableCell className="font-mono text-sm">{caseItem.id}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{caseItem.employee_name || 'N/A'}</p>
                          <p className="text-xs text-slate-500">{caseItem.employee_email || ''}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{caseItem.client_name || 'N/A'}</p>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-red-100 text-red-700">
                          {getStopReasonLabel(caseItem.stop_reason)}
                        </Badge>
                        {caseItem.stop_description && (
                          <p className="text-xs text-slate-500 mt-1 max-w-[200px] truncate" title={caseItem.stop_description}>
                            {caseItem.stop_description}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {caseItem.stopped_at ? new Date(caseItem.stopped_at).toLocaleString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-slate-100 text-slate-700">{stoppedChecks} checks</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => onViewCase(caseItem.id)}>
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
